// src/admin/components/services/ServicePaymentGeneration.jsx - Enhanced Version with Lists
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  RiMoneyDollarCircleLine,
  RiArrowLeftLine,
  RiCalendarLine,
  RiBuildingLine,
  RiCheckLine,
  RiSaveLine,
  RiErrorWarningLine,
  RiInformationLine,
  RiUserLine,
  RiSearchLine,
  RiAddLine,
  RiCloseCircleLine,
  RiUserStarLine,
  RiCloseLine,
  RiFileTextLine,
  RiFilterLine,
  RiCurrencyLine,
  RiHome4Line,
  RiTeamLine,
  RiCalendarCheckLine,
  RiCalendar2Line,
} from 'react-icons/ri';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  generateServicePayments,
  formatCurrency,
  getPaymentStatusColor,
  getServicePaymentById
} from '../../../models/serviceModel';
import { getAllBuildings } from '../../../models/buildingModel';
import { getAllTenants, formatTenantDisplayName } from '../../../models/tenantModel';
import { getAllOwners, formatOwnerDisplayName } from '../../../models/ownerModel';
import PrintServiceReceipt from './PrintServiceReceipt';

const ServicePaymentGeneration = () => {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [buildings, setBuildings] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [owners, setOwners] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [showAllEntities, setShowAllEntities] = useState(true);

  // Print receipt state
  const [showReceipt, setShowReceipt] = useState(false);
  const [paymentToPrint, setPaymentToPrint] = useState(null);

  const [loadingData, setLoadingData] = useState({
    buildings: true,
    tenants: false,
    owners: false
  });

  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('tenant'); // 'tenant' or 'owner'

  // Current date for default values
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();

  // Available months for the current year
  const allMonths = [
    { id: 1, name: t('january') },
    { id: 2, name: t('february') },
    { id: 3, name: t('march') },
    { id: 4, name: t('april') },
    { id: 5, name: t('may') },
    { id: 6, name: t('june') },
    { id: 7, name: t('july') },
    { id: 8, name: t('august') },
    { id: 9, name: t('september') },
    { id: 10, name: t('october') },
    { id: 11, name: t('november') },
    { id: 12, name: t('december') }
  ];

  // Currency options
  const currencies = [
    { code: 'USD', symbol: '$', name: t('us_dollar') },
    { code: 'IQD', symbol: 'د.ع', name: t('iraqi_dinar') },
    { code: 'EUR', symbol: '€', name: t('euro') }
  ];

  // Form state with enhanced options
  const [formData, setFormData] = useState({
    // Basic Info
    buildingId: '',

    // Recipient Type (all building apartments, specific tenants, or specific owners)
    recipientType: 'building', // 'building', 'tenant', 'owner'
    selectedRecipients: [], // array of tenant or owner IDs

    // Payment Details
    amount: '',
    currency: 'USD',

    // Time Period
    generationType: 'single', // 'single', 'multiple', 'range'
    selectedMonths: [currentDate.getMonth() + 1], // for multiple months (and single default)
    rangeStartMonth: 1,
    rangeEndMonth: 12,
    year: currentYear,

    // Additional Settings
    status: 'unpaid', // 'paid' or 'unpaid' when created
    notes: '',
    includeVacant: true // whether to include vacant apartments
  });

  // Load initial data
  useEffect(() => {
    const fetchBuildings = async () => {
      try {
        setLoadingData(prev => ({ ...prev, buildings: true }));
        const response = await getAllBuildings();

        const buildingsList = response.buildings || [];
        setBuildings(buildingsList);

        // If buildings are available, select the first one by default
        if (buildingsList.length > 0) {
          setFormData(prev => ({
            ...prev,
            buildingId: buildingsList[0].id
          }));
        }
      } catch (err) {
        console.error('Error loading buildings:', err);
        setError(t('failed_to_load_buildings'));
        setBuildings([]);
      } finally {
        setLoadingData(prev => ({ ...prev, buildings: false }));
      }
    };

    fetchBuildings();
  }, [t]);

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });

    // Reset additional fields when recipient type changes
    if (name === 'recipientType') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        selectedRecipients: []
      }));

      // Load the corresponding entity data when recipient type changes
      if (value === 'tenant') {
        loadTenants();
      } else if (value === 'owner') {
        loadOwners();
      }
    }

    // Reset selected months when generation type changes
    if (name === 'generationType') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        selectedMonths: value === 'single' ? [currentDate.getMonth() + 1] : []
      }));
    }

    // Clear error when input changes
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  // Handle month selection
  const handleMonthSelection = (monthId) => {
    setFormData(prev => {
      const months = [...prev.selectedMonths];

      if (months.includes(monthId)) {
        // Remove month if already selected
        return {
          ...prev,
          selectedMonths: months.filter(id => id !== monthId)
        };
      } else {
        // Add month if not selected
        return {
          ...prev,
          selectedMonths: [...months, monthId].sort((a, b) => a - b)
        };
      }
    });
  };

  // Load tenants for search
  const loadTenants = async () => {
    if (tenants.length > 0) return; // Only load once

    try {
      setLoadingData(prev => ({ ...prev, tenants: true }));
      const response = await getAllTenants();

      // Process the response to get the array of tenants
      let tenantsList = [];
      if (response && response.success) {
        // Handle nested structure if present
        if (response.tenants && typeof response.tenants === 'object' && Array.isArray(response.tenants.tenants)) {
          tenantsList = response.tenants.tenants;
        } else if (Array.isArray(response.tenants)) {
          tenantsList = response.tenants;
        }
      }

      setTenants(tenantsList);
      console.log("Loaded tenants:", tenantsList);
    } catch (err) {
      console.error('Error loading tenants:', err);
      setError(t('failed_to_load_tenants'));
      setTenants([]);
    } finally {
      setLoadingData(prev => ({ ...prev, tenants: false }));
    }
  };

  // Load owners for search
  const loadOwners = async () => {
    if (owners.length > 0) return; // Only load once

    try {
      setLoadingData(prev => ({ ...prev, owners: true }));
      const response = await getAllOwners();

      // Process the response to get the array of owners
      let ownersList = [];
      if (response && response.success) {
        // Handle nested structure if present
        if (response.owners && typeof response.owners === 'object' && Array.isArray(response.owners.owners)) {
          ownersList = response.owners.owners;
        } else if (Array.isArray(response.owners)) {
          ownersList = response.owners;
        }
      }

      setOwners(ownersList);
      console.log("Loaded owners:", ownersList);
    } catch (err) {
      console.error('Error loading owners:', err);
      setError(t('failed_to_load_owners'));
      setOwners([]);
    } finally {
      setLoadingData(prev => ({ ...prev, owners: false }));
    }
  };

  // Open search dialog
  const openSearchDialog = (type) => {
    setSearchType(type);
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchDialog(true);
    setShowAllEntities(true);

    // Load data if needed
    if (type === 'tenant') {
      loadTenants();
    } else {
      loadOwners();
    }

    // Focus the search input when dialog opens
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  };

  // Close search dialog
  const closeSearchDialog = () => {
    setShowSearchDialog(false);
  };

  // Handle search
  const handleSearch = () => {
    const query = searchQuery.toLowerCase();

    if (query.trim() === '') {
      setShowAllEntities(true);
      setSearchResults([]);
      return;
    }

    setShowAllEntities(false);

    if (searchType === 'tenant') {
      // Make sure we're working with an array
      const tenantsList = Array.isArray(tenants) ? tenants : [];

      const results = tenantsList.filter(tenant => {
        const name = formatTenantDisplayName(tenant).toLowerCase();
        const phone = (tenant.phone || '').toLowerCase();
        const aptNum = (tenant.apartmentNumber || '').toString().toLowerCase();

        return name.includes(query) || phone.includes(query) || aptNum.includes(query);
      });

      setSearchResults(results);
    } else {
      // Make sure we're working with an array
      const ownersList = Array.isArray(owners) ? owners : [];

      const results = ownersList.filter(owner => {
        const name = formatOwnerDisplayName(owner).toLowerCase();
        const phone = (owner.phone || '').toLowerCase();

        return name.includes(query) || phone.includes(query);
      });

      setSearchResults(results);
    }
  };

  // Handle search input change
  const handleSearchInputChange = (e) => {
    setSearchQuery(e.target.value);

    // If search field is cleared, show all entities
    if (e.target.value.trim() === '') {
      setShowAllEntities(true);
      setSearchResults([]);
    }
  };

  // Handle search input keypress
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Select recipient from search results or list
  const selectRecipient = (recipient) => {
    setFormData(prev => {
      // Check if recipient is already selected
      const isSelected = prev.selectedRecipients.some(r => r.id === recipient.id);

      if (isSelected) {
        return prev; // Do nothing if already selected
      }

      // Add recipient to selected list
      return {
        ...prev,
        selectedRecipients: [
          ...prev.selectedRecipients,
          {
            id: recipient.id,
            name: searchType === 'tenant' ? formatTenantDisplayName(recipient) : formatOwnerDisplayName(recipient),
            type: searchType,
            apartmentNumber: recipient.apartmentNumber || '',
            buildingName: recipient.buildingName || ''
          }
        ]
      };
    });
  };

  // Remove selected recipient
  const removeRecipient = (recipientId) => {
    setFormData(prev => ({
      ...prev,
      selectedRecipients: prev.selectedRecipients.filter(r => r.id !== recipientId)
    }));
  };

  // Get months for API request
  const getMonthsForRequest = () => {
    if (formData.generationType === 'single') {
      // Default to current month if none selected
      return formData.selectedMonths.length > 0
        ? formData.selectedMonths
        : [currentDate.getMonth() + 1];
    } else if (formData.generationType === 'multiple') {
      return formData.selectedMonths;
    } else if (formData.generationType === 'range') {
      // Create array of months in the range
      const months = [];
      for (let i = formData.rangeStartMonth; i <= formData.rangeEndMonth; i++) {
        months.push(i);
      }
      return months;
    }

    return [];
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Validate form
      if (formData.recipientType === 'building' && !formData.buildingId) {
        throw new Error(t('please_select_building'));
      }

      if ((formData.recipientType === 'tenant' || formData.recipientType === 'owner') &&
        formData.selectedRecipients.length === 0) {
        throw new Error(t('please_select_at_least_one_recipient', { recipient: t(formData.recipientType) }));
      }

      if (!formData.amount || parseFloat(formData.amount) <= 0) {
        throw new Error(t('please_enter_valid_amount'));
      }

      // Get the months to process based on generation type
      let months = [];
      if (formData.generationType === 'single') {
        if (formData.selectedMonths.length === 0) {
          throw new Error(t('please_select_month'));
        }
        months = [formData.selectedMonths[0]];
      } else if (formData.generationType === 'multiple') {
        if (formData.selectedMonths.length === 0) {
          throw new Error(t('please_select_at_least_one_month'));
        }
        months = formData.selectedMonths;
      } else if (formData.generationType === 'range') {
        if (formData.rangeStartMonth > formData.rangeEndMonth) {
          throw new Error(t('end_month_must_be_after_start_month'));
        }

        // Create array of months in the range
        for (let i = formData.rangeStartMonth; i <= formData.rangeEndMonth; i++) {
          months.push(i);
        }
      }

      // Create a payload with all necessary data
      const payload = {
        buildingId: formData.buildingId,
        months: months,
        year: parseInt(formData.year),
        amount: parseFloat(formData.amount),
        recipientType: formData.recipientType,
        includeVacant: formData.includeVacant,
        status: formData.status, // Include the status
        notes: formData.notes
      };

      // Add selected recipients if not generating for a building
      if (formData.recipientType !== 'building') {
        payload.selectedRecipients = formData.selectedRecipients.map(r => r.id);
      }

      console.log('Generating service payments with payload:', payload);

      // Generate service payments
      const response = await generateServicePayments(payload);

      if (response.success) {
        setSuccess(t('successfully_generated_payments', {
          count: response.generatedCount,
          months: months.length,
          skipped: response.skippedCount || 0
        }));

        // Reset form partially
        setFormData({
          ...formData,
          amount: '', // Only reset amount, keep other settings
          selectedMonths: [],
          notes: ''
        });

        // Handle printing if at least one payment was generated
        if (response.generatedIds && response.generatedIds.length > 0) {
          try {
            // Get the first generated payment to show a receipt
            const firstPaymentId = response.generatedIds[0];
            const paymentDetails = await getServicePaymentById(firstPaymentId);

            if (paymentDetails && paymentDetails.success && paymentDetails.payment) {
              setPaymentToPrint(paymentDetails.payment);
              setShowReceipt(true);
            }
          } catch (printErr) {
            console.error('Error loading payment for printing:', printErr);
            // Don't show error to user for printing failure if generation succeeded
          }
        }
      } else {
        throw new Error(response.message || t('failed_to_generate_service_payments'));
      }
    } catch (err) {
      console.error('Error generating service payments:', err);
      setError(err.message || t('failed_to_generate_service_payments'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-lg ${isRTL ? 'rtl' : 'ltr'}`}>
      {/* Header */}
      <div className={`p-5 border-b ${isDarkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-purple-50'}`}>
        <div className="flex justify-between items-center">
          <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'} flex items-center`}>
            <RiMoneyDollarCircleLine className={`${isRTL ? 'ml-3' : 'mr-3'} text-2xl ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`} />
            {t('generate_service_payments')}
          </h2>
          <button
            onClick={() => navigate('/admin/services')}
            className={`p-2 rounded-full ${isDarkMode ? 'bg-zinc-800 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'}`}
          >
            <RiArrowLeftLine size={22} />
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className={`mx-5 mt-5 p-4 rounded-lg ${isDarkMode ? 'bg-red-900/20 border-red-800 text-red-400' : 'bg-zinc-50 border-red-200 text-red-700'} border flex items-center`}>
          <RiErrorWarningLine className={`${isRTL ? 'ml-2' : 'mr-2'} text-xl`} />
          {error}
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className={`mx-5 mt-5 p-4 rounded-lg ${isDarkMode ? 'bg-green-900/20 border-green-800 text-green-400' : 'bg-green-50 border-green-200 text-green-700'} border flex items-center`}>
          <RiCheckLine className={`${isRTL ? 'ml-2' : 'mr-2'} text-xl`} />
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-5">
        {/* Recipient Selection */}
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'} mb-6`}>
          <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center`}>
            <RiUserLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('recipients')}
          </h3>

          <div className="space-y-4">
            {/* Recipient Type Selection */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div
                className={`p-4 rounded-lg border-2 cursor-pointer ${formData.recipientType === 'building'
                  ? (isDarkMode ? 'border-purple-500 bg-purple-900/20' : 'border-purple-500 bg-purple-50')
                  : (isDarkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300')
                  }`}
                onClick={() => handleInputChange({ target: { name: 'recipientType', value: 'building' } })}
              >
                <div className="flex items-center">
                  <div className={`p-2 rounded-full ${formData.recipientType === 'building'
                    ? (isDarkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-600')
                    : (isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-gray-100 text-gray-500')
                    }`}>
                    <RiBuildingLine size={20} />
                  </div>
                  <div className={`${isRTL ? 'mr-3' : 'ml-3'}`}>
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {t('entire_building')}
                    </p>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                      {t('generate_for_all_apartments')}
                    </p>
                  </div>
                </div>
              </div>

              <div
                className={`p-4 rounded-lg border-2 cursor-pointer ${formData.recipientType === 'tenant'
                  ? (isDarkMode ? 'border-purple-500 bg-purple-900/20' : 'border-purple-500 bg-purple-50')
                  : (isDarkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300')
                  }`}
                onClick={() => handleInputChange({ target: { name: 'recipientType', value: 'tenant' } })}
              >
                <div className="flex items-center">
                  <div className={`p-2 rounded-full ${formData.recipientType === 'tenant'
                    ? (isDarkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-600')
                    : (isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-gray-100 text-gray-500')
                    }`}>
                    <RiTeamLine size={20} />
                  </div>
                  <div className={`${isRTL ? 'mr-3' : 'ml-3'}`}>
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {t('specific_tenants')}
                    </p>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                      {t('generate_for_selected_tenants')}
                    </p>
                  </div>
                </div>
              </div>

              <div
                className={`p-4 rounded-lg border-2 cursor-pointer ${formData.recipientType === 'owner'
                  ? (isDarkMode ? 'border-purple-500 bg-purple-900/20' : 'border-purple-500 bg-purple-50')
                  : (isDarkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300')
                  }`}
                onClick={() => handleInputChange({ target: { name: 'recipientType', value: 'owner' } })}
              >
                <div className="flex items-center">
                  <div className={`p-2 rounded-full ${formData.recipientType === 'owner'
                    ? (isDarkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-600')
                    : (isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-gray-100 text-gray-500')
                    }`}>
                    <RiUserStarLine size={20} />
                  </div>
                  <div className={`${isRTL ? 'mr-3' : 'ml-3'}`}>
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {t('specific_owners')}
                    </p>
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                      {t('generate_for_selected_owners')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Building Selection (when recipientType is 'building') */}
            {formData.recipientType === 'building' && (
              <div className="mt-4">
                <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('select_building')} *
                </label>
                <select
                  name="buildingId"
                  value={formData.buildingId}
                  onChange={handleInputChange}
                  className={`w-full p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  required={formData.recipientType === 'building'}
                  disabled={loadingData.buildings}
                >
                  <option value="">{t('select_building')}</option>
                  {buildings.map(building => (
                    <option key={building.id} value={building.id}>
                      {building.name}
                    </option>
                  ))}
                </select>

                {/* Include Vacant Apartments Option */}
                <div className="mt-3 flex items-center">
                  <input
                    type="checkbox"
                    id="includeVacant"
                    name="includeVacant"
                    checked={formData.includeVacant}
                    onChange={handleInputChange}
                    className={`h-4 w-4 rounded ${isDarkMode ? 'bg-zinc-800 border-gray-600' : 'bg-white border-gray-300'
                      } text-purple-600 focus:ring-purple-500`}
                  />
                  <label
                    htmlFor="includeVacant"
                    className={`${isRTL ? 'mr-2' : 'ml-2'} text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
                  >
                    {t('include_vacant_apartments')}
                  </label>
                </div>
              </div>
            )}

            {/* Tenant/Owner Selection (when recipientType is 'tenant' or 'owner') */}
            {(formData.recipientType === 'tenant' || formData.recipientType === 'owner') && (
              <div className="mt-4">
                <div className="flex justify-between items-center mb-2">
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {formData.recipientType === 'tenant'
                      ? t('selected_tenants')
                      : t('selected_owners')}
                    {formData.selectedRecipients.length > 0 && ` (${formData.selectedRecipients.length})`}
                  </label>

                  <button
                    type="button"
                    onClick={() => openSearchDialog(formData.recipientType)}
                    className={`px-3 py-1.5 text-xs rounded-lg flex items-center ${isDarkMode ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-purple-500 text-white hover:bg-purple-600'
                      }`}
                  >
                    <RiCheckLine className={`${isRTL ? 'ml-1' : 'mr-1'}`} size={14} />
                    {formData.recipientType === 'tenant'
                      ? t('select_tenants')
                      : t('select_owners')}
                  </button>
                </div>

                {formData.selectedRecipients.length === 0 ? (
                  <div className={`p-4 rounded-lg border border-dashed ${isDarkMode ? 'border-gray-600 text-zinc-400' : 'border-gray-300 text-gray-500'
                    } text-center`}>
                    {formData.recipientType === 'tenant'
                      ? t('no_tenants_selected')
                      : t('no_owners_selected')}
                  </div>
                ) : (
                  <div className={`p-4 rounded-lg border ${isDarkMode ? 'border-gray-600 bg-zinc-800' : 'border-gray-200 bg-gray-50'
                    }`}>
                    <div className="max-h-40 overflow-y-auto space-y-2">
                      {formData.selectedRecipients.map(recipient => (
                        <div
                          key={recipient.id}
                          className={`p-2 rounded-lg flex justify-between items-center ${isDarkMode ? 'bg-gray-650 hover:bg-gray-600' : 'bg-white'}`}
                        >
                          <div className="flex items-center">
                            <div className={`p-1.5 rounded-full ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-600'
                              }`}>
                              {recipient.type === 'tenant' ? (
                                <RiTeamLine size={16} />
                              ) : (
                                <RiUserStarLine size={16} />
                              )}
                            </div>
                            <div className={`${isRTL ? 'mr-2' : 'ml-2'}`}>
                              <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {recipient.name}
                              </p>
                              {recipient.apartmentNumber && (
                                <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                                  {t('apt')} {recipient.apartmentNumber}{recipient.buildingName ? ` - ${recipient.buildingName}` : ''}
                                </p>
                              )}
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => removeRecipient(recipient.id)}
                            className={`p-1 rounded-full ${isDarkMode ? 'text-zinc-400 hover:bg-zinc-800 hover:text-red-400' : 'text-gray-500 hover:bg-gray-200 hover:text-red-500'
                              }`}
                          >
                            <RiCloseLine size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Payment Details */}
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'} mb-6`}>
          <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center`}>
            <RiMoneyDollarCircleLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('payment_details')}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Amount */}
            <div>
              <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('amount')} *
              </label>
              <div className="relative">
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  className={`w-full p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300'
                    } ${formData.currency === 'IQD' ? 'pl-14' : 'pl-8'}`}
                  required
                  min="0.01"
                  step="0.01"
                />
                <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none text-${isDarkMode ? 'gray-400' : 'gray-500'
                  }`}>
                  {currencies.find(c => c.code === formData.currency)?.symbol}
                </div>
              </div>
            </div>

            {/* Currency */}
            <div>
              <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('currency')}
              </label>
              <div className="flex space-x-2">
                {currencies.map(currency => (
                  <button
                    key={currency.code}
                    type="button"
                    onClick={() => handleInputChange({ target: { name: 'currency', value: currency.code } })}
                    className={`py-2 px-3 rounded-lg flex items-center ${formData.currency === currency.code
                      ? (isDarkMode ? 'bg-purple-600 text-white' : 'bg-purple-500 text-white')
                      : (isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')
                      }`}
                  >
                    <span className="text-sm font-medium">{currency.symbol} {currency.code}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Initial Status */}
            <div>
              <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('initial_status')}
              </label>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={() => handleInputChange({ target: { name: 'status', value: 'unpaid' } })}
                  className={`py-2 px-3 rounded-lg flex items-center ${formData.status === 'unpaid'
                    ? (isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400 border border-red-800' : 'bg-zinc-50 text-red-700 border border-red-200')
                    : (isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')
                    }`}
                >
                  <RiCloseCircleLine className={`${isRTL ? 'ml-1.5' : 'mr-1.5'} ${formData.status === 'unpaid' ? '' : ''}`} size={16} />
                  <span className="text-sm font-medium">{t('unpaid')}</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleInputChange({ target: { name: 'status', value: 'paid' } })}
                  className={`py-2 px-3 rounded-lg flex items-center ${formData.status === 'paid'
                    ? (isDarkMode ? 'bg-green-900 bg-opacity-20 text-green-400 border border-green-800' : 'bg-green-50 text-green-700 border border-green-200')
                    : (isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')
                    }`}
                >
                  <RiCheckLine className={`${isRTL ? 'ml-1.5' : 'mr-1.5'} ${formData.status === 'paid' ? '' : ''}`} size={16} />
                  <span className="text-sm font-medium">{t('paid')}</span>
                </button>
              </div>
            </div>

            {/* Notes */}
            <div className="md:col-span-2">
              <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('notes')}
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={2}
                className={`w-full p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                placeholder={t('payment_notes_placeholder')}
              />
            </div>
          </div>
        </div>

        {/* Time Period */}
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'} mb-6`}>
          <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center`}>
            <RiCalendarLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('time_period')}
          </h3>

          <div className="space-y-4">
            {/* Generation Type Selection */}
            <div>
              <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('generation_type')}
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  type="button"
                  onClick={() => handleInputChange({ target: { name: 'generationType', value: 'single' } })}
                  className={`p-3 rounded-lg border-2 flex items-center ${formData.generationType === 'single'
                    ? (isDarkMode ? 'border-purple-500 bg-purple-900/20' : 'border-purple-500 bg-purple-50')
                    : (isDarkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300')
                    }`}
                >
                  <div className={`p-1.5 rounded-full ${formData.generationType === 'single'
                    ? (isDarkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-600')
                    : (isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-gray-100 text-gray-500')
                    }`}>
                    <RiCalendarLine size={18} />
                  </div>
                  <span className={`${isRTL ? 'mr-2' : 'ml-2'} text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {t('single_month')}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => handleInputChange({ target: { name: 'generationType', value: 'multiple' } })}
                  className={`p-3 rounded-lg border-2 flex items-center ${formData.generationType === 'multiple'
                    ? (isDarkMode ? 'border-purple-500 bg-purple-900/20' : 'border-purple-500 bg-purple-50')
                    : (isDarkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300')
                    }`}
                >
                  <div className={`p-1.5 rounded-full ${formData.generationType === 'multiple'
                    ? (isDarkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-600')
                    : (isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-gray-100 text-gray-500')
                    }`}>
                    <RiCalendarCheckLine size={18} />
                  </div>
                  <span className={`${isRTL ? 'mr-2' : 'ml-2'} text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {t('multiple_months')}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => handleInputChange({ target: { name: 'generationType', value: 'range' } })}
                  className={`p-3 rounded-lg border-2 flex items-center ${formData.generationType === 'range'
                    ? (isDarkMode ? 'border-purple-500 bg-purple-900/20' : 'border-purple-500 bg-purple-50')
                    : (isDarkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300')
                    }`}
                >
                  <div className={`p-1.5 rounded-full ${formData.generationType === 'range'
                    ? (isDarkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-100 text-purple-600')
                    : (isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-gray-100 text-gray-500')
                    }`}>
                    <RiCalendar2Line size={18} />
                  </div>
                  <span className={`${isRTL ? 'mr-2' : 'ml-2'} text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {t('month_range')}
                  </span>
                </button>
              </div>
            </div>

            {/* Year Selection */}
            <div>
              <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('year')}
              </label>
              <select
                name="year"
                value={formData.year}
                onChange={handleInputChange}
                className={`w-full sm:w-40 p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
              >
                {Array.from({ length: 5 }, (_, i) => currentYear - 2 + i).map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            {/* Month Selection UI based on generationType */}
            {formData.generationType === 'single' && (
              <div>
                <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('month')}
                </label>
                <select
                  name="selectedMonths"
                  value={formData.selectedMonths[0] || currentDate.getMonth() + 1}
                  onChange={(e) => setFormData({
                    ...formData,
                    selectedMonths: [parseInt(e.target.value)]
                  })}
                  className={`w-full sm:w-40 p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                >
                  {allMonths.map(month => (
                    <option key={month.id} value={month.id}>
                      {month.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {formData.generationType === 'multiple' && (
              <div>
                <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('select_months')} {formData.selectedMonths.length > 0 && `(${formData.selectedMonths.length})`}
                </label>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                  {allMonths.map(month => (
                    <button
                      key={month.id}
                      type="button"
                      onClick={() => handleMonthSelection(month.id)}
                      className={`p-2 rounded-lg border ${formData.selectedMonths.includes(month.id)
                        ? (isDarkMode ? 'bg-purple-900/30 text-purple-400 border-purple-700' : 'bg-purple-100 text-purple-600 border-purple-300')
                        : (isDarkMode ? 'bg-zinc-800 text-gray-300 border-gray-600 hover:bg-gray-650' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50')
                        }`}
                    >
                      {month.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {formData.generationType === 'range' && (
              <div>
                <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('month_range')}
                </label>
                <div className="flex items-center space-x-4">
                  <div>
                    <label className={`block mb-1 text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                      {t('from')}
                    </label>
                    <select
                      name="rangeStartMonth"
                      value={formData.rangeStartMonth}
                      onChange={handleInputChange}
                      className={`w-full p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300'
                        }`}
                    >
                      {allMonths.map(month => (
                        <option key={month.id} value={month.id}>
                          {month.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block mb-1 text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                      {t('to')}
                    </label>
                    <select
                      name="rangeEndMonth"
                      value={formData.rangeEndMonth}
                      onChange={handleInputChange}
                      className={`w-full p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300'
                        }`}
                    >
                      {allMonths.map(month => (
                        <option key={month.id} value={month.id} disabled={month.id < formData.rangeStartMonth}>
                          {month.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Display selected months in range */}
                {formData.rangeStartMonth <= formData.rangeEndMonth && (
                  <div className="mt-2">
                    <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                      {t('selected_months')}:
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {Array.from(
                        { length: formData.rangeEndMonth - formData.rangeStartMonth + 1 },
                        (_, i) => formData.rangeStartMonth + i
                      ).map(monthId => (
                        <span
                          key={monthId}
                          className={`px-2 py-0.5 text-xs rounded-full ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-700'
                            }`}
                        >
                          {allMonths.find(m => m.id === monthId)?.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Summary */}
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-red-900/10 border-red-800' : 'bg-zinc-50 border-red-200'} border mb-6`}>
          <div className="flex items-start">
            <RiInformationLine className={`text-xl ${isRTL ? 'ml-2' : 'mr-2'} mt-0.5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
            <div>
              <h4 className={`font-medium ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>
                {t('payment_generation_summary')}
              </h4>
              <div className={`mt-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <p className="mb-1">
                  <span className="font-medium">{t('recipients')}:</span> {' '}
                  {formData.recipientType === 'building'
                    ? `${t('all_apartments_in')} ${buildings.find(b => b.id === formData.buildingId)?.name || t('selected_building')}`
                    : `${formData.selectedRecipients.length} ${formData.recipientType === 'tenant' ? t('tenants') : t('owners')}`
                  }
                </p>

                <p className="mb-1">
                  <span className="font-medium">{t('amount')}:</span> {' '}
                  {formData.amount ? `${currencies.find(c => c.code === formData.currency)?.symbol}${formData.amount} ${formData.currency}` : '-'}
                </p>

                <p className="mb-1">
                  <span className="font-medium">{t('time_period')}:</span> {' '}
                  {(() => {
                    const year = formData.year;

                    if (formData.generationType === 'single') {
                      const monthId = formData.selectedMonths[0] || currentDate.getMonth() + 1;
                      const month = allMonths.find(m => m.id === monthId)?.name || '';
                      return `${month} ${year}`;
                    } else if (formData.generationType === 'multiple') {
                      if (formData.selectedMonths.length === 0) return '-';

                      const monthNames = formData.selectedMonths
                        .map(id => allMonths.find(m => m.id === id)?.name)
                        .join(', ');

                      return `${monthNames} ${year}`;
                    } else if (formData.generationType === 'range') {
                      const startMonth = allMonths.find(m => m.id === formData.rangeStartMonth)?.name;
                      const endMonth = allMonths.find(m => m.id === formData.rangeEndMonth)?.name;
                      return `${startMonth} - ${endMonth} ${year}`;
                    }

                    return '-';
                  })()}
                </p>

                <p>
                  <span className="font-medium">{t('initial_status')}:</span> {' '}
                  <span className={`px-2 py-0.5 rounded-full text-xs ${formData.status === 'paid'
                    ? (isDarkMode ? 'bg-green-900 bg-opacity-30 text-green-400' : 'bg-green-100 text-green-800')
                    : (isDarkMode ? 'bg-red-900 bg-opacity-30 text-red-400' : 'bg-zinc-100 text-red-800')
                    }`}>
                    {formData.status === 'paid' ? t('paid') : t('unpaid')}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/admin/services')}
            className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-zinc-800 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} transition-colors`}
            disabled={loading}
          >
            <RiArrowLeftLine className={`${isRTL ? 'ml-1' : 'mr-1'} inline`} size={18} />
            {t('cancel')}
          </button>

          <button
            type="submit"
            className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-purple-600 hover:bg-purple-700' : 'bg-purple-500 hover:bg-purple-600'} text-white transition-colors flex items-center`}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                {t('generating')}
              </>
            ) : (
              <>
                <RiSaveLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} size={18} />
                {t('generate_payments')}
              </>
            )}
          </button>
        </div>
      </form>

      {/* Search Dialog */}
      {showSearchDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className={`max-w-2xl w-full rounded-xl shadow-lg ${isDarkMode ? 'bg-zinc-900 border-gray-700' : 'bg-white border-gray-200'} border ${isRTL ? 'rtl' : 'ltr'}`}>
            <div className={`p-4 ${isDarkMode ? 'border-b border-gray-700' : 'border-b border-gray-200'}`}>
              <div className="flex justify-between items-center">
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {searchType === 'tenant'
                    ? t('select_tenants')
                    : t('select_owners')}
                </h3>
                <button
                  onClick={closeSearchDialog}
                  className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-zinc-800 text-zinc-400' : 'hover:bg-gray-100 text-gray-500'}`}
                >
                  <RiCloseLine size={20} />
                </button>
              </div>
            </div>

            <div className="p-4">
              {/* Search Box */}
              <div className="relative mb-4">
                <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none`}>
                  <RiSearchLine className={isDarkMode ? 'text-zinc-400' : 'text-gray-500'} />
                </div>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  onKeyPress={handleSearchKeyPress}
                  className={`w-full ${isRTL ? 'pr-10 pl-14' : 'pl-10 pr-14'} py-2 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  placeholder={searchType === 'tenant'
                    ? t('search_by_tenant_name_or_phone')
                    : t('search_by_owner_name_or_phone')}
                />
                <div className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-3' : 'right-0 pr-3'} flex items-center`}>
                  <button
                    onClick={handleSearch}
                    className={`px-2 py-1 rounded ${isDarkMode ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    {t('search')}
                  </button>
                </div>
              </div>

              {/* Loading State */}
              {loadingData[searchType === 'tenant' ? 'tenants' : 'owners'] && (
                <div className="flex justify-center py-8">
                  <div className={`inline-block h-8 w-8 animate-spin rounded-full border-4 ${isDarkMode ? 'border-purple-500 border-t-transparent' : 'border-purple-500 border-t-transparent'
                    }`}></div>
                </div>
              )}

              {/* List Display */}
              {!loadingData[searchType === 'tenant' ? 'tenants' : 'owners'] && (
                <div className={`max-h-80 overflow-y-auto ${isDarkMode ? 'border border-gray-700' : 'border border-gray-200'
                  } rounded-lg`}>
                  {/* Show search results if there are any, otherwise show all items */}
                  {searchQuery && !showAllEntities ? (
                    searchResults.length === 0 ? (
                      <div className={`p-4 text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {t('no_results_found')}
                      </div>
                    ) : (
                      <EntityList
                        entities={searchResults}
                        selectedIds={formData.selectedRecipients.map(r => r.id)}
                        onSelect={selectRecipient}
                        entityType={searchType}
                        isDarkMode={isDarkMode}
                        isRTL={isRTL}
                        t={t}
                      />
                    )
                  ) : (
                    // Show all entities
                    <EntityList
                      entities={searchType === 'tenant' ? tenants : owners}
                      selectedIds={formData.selectedRecipients.map(r => r.id)}
                      onSelect={selectRecipient}
                      entityType={searchType}
                      isDarkMode={isDarkMode}
                      isRTL={isRTL}
                      t={t}
                    />
                  )}
                </div>
              )}
            </div>

            <div className={`p-4 ${isDarkMode ? 'border-t border-gray-700' : 'border-t border-gray-200'}`}>
              <div className="flex justify-between items-center">
                <span className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                  {formData.selectedRecipients.length} {t('selected')}
                </span>
                <div className="space-x-2">
                  <button
                    type="button"
                    onClick={closeSearchDialog}
                    className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    {t('cancel')}
                  </button>

                  <button
                    type="button"
                    onClick={closeSearchDialog}
                    className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-purple-500 text-white hover:bg-purple-600'
                      }`}
                  >
                    {t('done')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Print Receipt Modal */}
      {showReceipt && paymentToPrint && (
        <PrintServiceReceipt
          payment={paymentToPrint}
          onClose={() => {
            setShowReceipt(false);
            setPaymentToPrint(null);
          }}
          autoPrint={true}
        />
      )}
    </div>
  );
};

// Helper component for displaying a list of entities (tenants or owners)
const EntityList = ({ entities, selectedIds, onSelect, entityType, isDarkMode, isRTL, t }) => {
  // Check if entities is an array and not empty
  if (!Array.isArray(entities) || entities.length === 0) {
    return (
      <div className={`p-4 text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        {entityType === 'tenant'
          ? t('no_tenants_available')
          : t('no_owners_available')}
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-200 dark:divide-gray-700">
      {entities.map(entity => {
        const isSelected = selectedIds.includes(entity.id);
        const displayName = entityType === 'tenant'
          ? (entity.name || `${entity.firstName || ''} ${entity.lastName || ''}`.trim())
          : (entity.name || '');

        return (
          <div
            key={entity.id}
            className={`p-3 hover:bg-gray-50 dark:hover:bg-zinc-800 cursor-pointer ${isSelected ? (isDarkMode ? 'bg-gray-750' : 'bg-gray-100') : ''
              }`}
            onClick={() => onSelect(entity)}
          >
            <div className="flex justify-between items-center">
              <div>
                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {displayName}
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                  {entity.phone}
                </p>

                {entityType === 'tenant' && entity.apartmentNumber && (
                  <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'} mt-1`}>
                    {t('apt')} {entity.apartmentNumber}{entity.buildingName ? ` - ${entity.buildingName}` : ''}
                  </p>
                )}
              </div>

              <button
                type="button"
                className={`p-2 rounded-full ${isSelected
                  ? (isDarkMode ? 'bg-purple-900 bg-opacity-30 text-purple-400' : 'bg-purple-100 text-purple-600')
                  : (isDarkMode ? 'bg-zinc-800 text-gray-300' : 'bg-gray-100 text-gray-600')
                  }`}
              >
                {isSelected ? (
                  <RiCheckLine size={18} />
                ) : (
                  <RiAddLine size={18} />
                )}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ServicePaymentGeneration;